import React, { useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { PageHeader, SectionCard, Pill } from "../components/Ui";
import DataTableBlock from "../components/DataTableBlock";
import DataTableExportButton from "../components/DataTableExportButton";
import { useAuth, isAdmin } from "../context/AuthContext";
import { usePaginatedList } from "../hooks/usePaginatedList";
import { LIST_PAGE_SIZE_OPTIONS } from "../lib/listPagination";
import { AUDIT_LOG_COLUMNS, auditStatusTone, formatAuditCell } from "./auditLogColumns";

const DETAIL_TABS = [
  { id: "changes", label: "Changes" },
  { id: "request", label: "Request" },
  { id: "response", label: "Response" },
  { id: "before", label: "Before" },
  { id: "after", label: "After" },
  { id: "meta", label: "Meta" },
];

export default function AuditLogsPage() {
  const { user } = useAuth();
  const admin = isAdmin(user);
  const [selected, setSelected] = useState(null);
  const [detailTab, setDetailTab] = useState("changes");

  const list = usePaginatedList({
    path: "/admin/audit-logs",
    columns: AUDIT_LOG_COLUMNS,
    defaultSort: { id: "created_at", desc: true },
    enabled: admin,
  });

  if (user && !admin) {
    return <Navigate to="/" replace />;
  }

  const selectRow = (row) => {
    setSelected((prev) => (prev?.id === row.id ? null : row));
    setDetailTab("changes");
  };

  return (
    <div className="space-y-4 min-w-0" data-testid="audit-logs-page">
      <PageHeader
        title="Audit log"
        description="Full trace of who changed what — request body, response body, before/after snapshots, and field-level diffs."
      />

      <DataTableBlock
        title="Activity log"
        columns={AUDIT_LOG_COLUMNS}
        table={list.table}
        rows={list.rows}
        loading={list.loading}
        totalCount={list.meta.count}
        pagination={{
          ...list.pagination,
          pageSizeOptions: LIST_PAGE_SIZE_OPTIONS,
        }}
        exportButton={(
          <DataTableExportButton
            path="/admin/audit-logs"
            columns={AUDIT_LOG_COLUMNS}
            table={list.table}
            filename="audit-log.xlsx"
            testId="audit-logs-export"
          />
        )}
        testId="audit-logs-table"
        minWidth="1080px"
        renderRow={(row) => (
          <tr
            key={row.id}
            className={`border-b border-border/60 hover:bg-muted/20 cursor-pointer ${selected?.id === row.id ? "bg-muted/50" : ""}`}
            onClick={() => selectRow(row)}
            data-testid={`audit-log-row-${row.id}`}
          >
            {AUDIT_LOG_COLUMNS.filter((c) => !c.hideInTable).map((col) => (
              <td key={col.id} className={`px-3 py-2.5 align-top text-sm ${col.className || ""}`}>
                {col.id === "actor_name" ? (
                  <div className="min-w-0">
                    <p className="font-medium truncate">{row.actor_name || "—"}</p>
                    {row.actor_email ? (
                      <p className="text-[11px] text-muted-foreground truncate">{row.actor_email}</p>
                    ) : null}
                  </div>
                ) : col.id === "status_code" ? (
                  <span className={auditStatusTone(row.status_code)}>{formatAuditCell(col, row)}</span>
                ) : col.id === "change_count" ? (
                  row.change_count > 0 ? (
                    <Pill tone="info">{row.change_count} field{row.change_count === 1 ? "" : "s"}</Pill>
                  ) : (
                    "—"
                  )
                ) : (
                  formatAuditCell(col, row)
                )}
              </td>
            ))}
          </tr>
        )}
      />

      {selected && (
        <SectionCard title={`Log detail · #${selected.id}`} testId="audit-log-detail">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm min-w-0 mb-4">
            <DetailField label="When" value={selected.created_at} />
            <DetailField label="User" value={`${selected.actor_name || "—"} (${selected.actor_role || "—"})`} />
            <DetailField label="Action" value={`${selected.action} · HTTP ${selected.status_code || "—"}`} />
            <DetailField label="Entity" value={entityLabel(selected)} />
            <DetailField label="Method" value={`${selected.method || "—"} ${selected.path || ""}`} className="sm:col-span-2 lg:col-span-4" />
            <DetailField label="Summary" value={selected.summary || "—"} className="sm:col-span-2 lg:col-span-4" />
          </div>

          <div className="flex flex-wrap gap-1.5 border-b border-border/70 pb-3 mb-4">
            {DETAIL_TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setDetailTab(tab.id)}
                className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                  detailTab === tab.id
                    ? "bg-bansal-sage text-white"
                    : "bg-muted/40 text-muted-foreground hover:bg-muted/70"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <AuditDetailPanel row={selected} tab={detailTab} />
        </SectionCard>
      )}
    </div>
  );
}

function entityLabel(row) {
  if (!row.entity_type && !row.entity_id) return "—";
  return `${row.entity_type || "—"}${row.entity_id ? ` #${row.entity_id}` : ""}${row.entity_label ? ` — ${row.entity_label}` : ""}`;
}

function AuditDetailPanel({ row, tab }) {
  if (tab === "changes") {
    const changes = row.changes || [];
    if (!changes.length) {
      return <EmptyDetail message="No field-level changes recorded for this request." />;
    }
    return (
      <div className="overflow-x-auto rounded-md border border-border">
        <table className="w-full text-xs min-w-[640px]">
          <thead>
            <tr className="bg-muted/40 text-left text-[10px] uppercase tracking-widest text-muted-foreground">
              <th className="px-3 py-2">Type</th>
              <th className="px-3 py-2">Field</th>
              <th className="px-3 py-2">Before</th>
              <th className="px-3 py-2">After</th>
            </tr>
          </thead>
          <tbody>
            {changes.map((change, idx) => (
              <tr key={`${change.field}-${idx}`} className="border-t border-border/60 align-top">
                <td className="px-3 py-2 whitespace-nowrap">
                  <ChangeTypePill type={change.change_type} />
                </td>
                <td className="px-3 py-2 font-mono">{change.field}</td>
                <td className="px-3 py-2"><JsonCell value={change.old} /></td>
                <td className="px-3 py-2"><JsonCell value={change.new} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  if (tab === "request") {
    return <JsonBlock title="Request payload" data={row.request_payload} empty="No request body captured." />;
  }
  if (tab === "response") {
    return <JsonBlock title="Response payload" data={row.response_payload} empty="No response body captured." />;
  }
  if (tab === "before") {
    return <JsonBlock title="Before (DB snapshot)" data={row.before} empty="No before-state snapshot." />;
  }
  if (tab === "after") {
    return <JsonBlock title="After (from response)" data={row.after} empty="No after-state captured." />;
  }

  return (
    <div className="space-y-3 text-sm">
      <DetailField label="Client IP" value={row.client_ip || "—"} />
      <DetailField label="Query params" value={formatJsonInline(row.query_params)} />
      {row.extra && Object.keys(row.extra).length > 0 ? (
        <JsonBlock title="Raw extra" data={row.extra} />
      ) : null}
    </div>
  );
}

function ChangeTypePill({ type }) {
  const tone = type === "added" ? "success" : type === "removed" ? "danger" : "warning";
  const label = type === "added" ? "Added" : type === "removed" ? "Removed" : "Modified";
  return <Pill tone={tone}>{label}</Pill>;
}

function JsonCell({ value }) {
  const text = useMemo(() => formatJsonInline(value), [value]);
  return <span className="break-words whitespace-pre-wrap text-muted-foreground">{text}</span>;
}

function JsonBlock({ title, data, empty = "Nothing to show." }) {
  if (data == null) {
    return <EmptyDetail message={empty} />;
  }
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-2">{title}</p>
      <pre className="rounded-md border border-border bg-muted/20 p-3 text-xs overflow-x-auto whitespace-pre-wrap break-words max-h-[420px] overflow-y-auto">
        {JSON.stringify(data, null, 2)}
      </pre>
    </div>
  );
}

function EmptyDetail({ message }) {
  return <p className="text-sm text-muted-foreground py-6 text-center">{message}</p>;
}

function formatJsonInline(value) {
  if (value == null) return "—";
  if (typeof value === "object") return JSON.stringify(value, null, 2);
  return String(value);
}

function DetailField({ label, value, className = "" }) {
  return (
    <div className={`min-w-0 ${className}`}>
      <p className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{label}</p>
      <p className="mt-1 break-words whitespace-pre-wrap">{value}</p>
    </div>
  );
}
